import { z } from "zod";
export const policyKindSchema = z.enum(["terms", "privacy", "guardian_consent"]);
export const policyEnvironmentSchema = z.enum(["staging", "production"]);
export const policyReleaseStatusSchema = z.enum(["active", "inactive"]);
const boundedIdentifierSchema = z
    .string()
    .min(1)
    .max(120)
    .regex(/^[a-z0-9](?:[a-z0-9._+-]*[a-z0-9])?$/);
const policyVersionSchema = z
    .string()
    .min(1)
    .max(100)
    .regex(/^[0-9A-Za-z](?:[0-9A-Za-z._+-]*[0-9A-Za-z])?$/);
export const sha256HexSchema = z.string().regex(/^[0-9a-f]{64}$/);
export const policySourcePathSchema = z
    .string()
    .min(1)
    .max(240)
    .refine(isNormalizedRelativePath, { error: "Path must be normalized and relative" });
export const policyReleaseFlagsSchema = z
    .strictObject({
    environment: policyEnvironmentSchema,
    approved: z.boolean(),
    publishable: z.boolean(),
    stagingFixture: z.boolean(),
    technicalAcceptanceOnly: z.boolean(),
    allowedEmailHashes: z.array(sha256HexSchema).max(100),
    acceptanceAllowed: z.boolean(),
    consentAllowed: z.boolean(),
})
    .superRefine((flags, context) => {
    if (new Set(flags.allowedEmailHashes).size !== flags.allowedEmailHashes.length) {
        context.addIssue({ code: "custom", path: ["allowedEmailHashes"], message: "Duplicate hash" });
    }
    if (flags.publishable &&
        (!flags.approved || flags.stagingFixture || flags.technicalAcceptanceOnly)) {
        context.addIssue({ code: "custom", message: "Publishable release flags are inconsistent" });
    }
    if (flags.technicalAcceptanceOnly &&
        (flags.environment !== "staging" ||
            flags.approved ||
            flags.publishable ||
            !flags.stagingFixture)) {
        context.addIssue({ code: "custom", message: "Technical release flags are inconsistent" });
    }
    if (flags.environment === "production" && flags.stagingFixture) {
        context.addIssue({
            code: "custom",
            message: "Production releases cannot be staging fixtures",
        });
    }
    if (flags.environment === "production" &&
        (flags.acceptanceAllowed || flags.consentAllowed) &&
        (!flags.approved || !flags.publishable)) {
        context.addIssue({
            code: "custom",
            message: "Actionable production policies must be approved and publishable",
        });
    }
});
export const policyContentSchema = z
    .strictObject({
    schema: z.literal("tutorlite.policy-content/v1"),
    kind: policyKindSchema,
    version: policyVersionSchema,
    locale: z.enum(["id", "en"]),
    title: z.string().trim().min(1).max(300),
    bodyFormat: z.literal("markdown"),
    bodyMarkdown: z.string().min(1).max(500_000),
    release: policyReleaseFlagsSchema,
})
    .superRefine((content, context) => {
    if (content.release.acceptanceAllowed && content.kind !== "terms") {
        context.addIssue({
            code: "custom",
            path: ["release", "acceptanceAllowed"],
            message: "Only terms can be accepted",
        });
    }
    if (content.release.consentAllowed && content.kind !== "guardian_consent") {
        context.addIssue({
            code: "custom",
            path: ["release", "consentAllowed"],
            message: "Only guardian consent can be consented",
        });
    }
});
export const policyRegistryDocumentSchema = z.strictObject({
    id: boundedIdentifierSchema,
    kind: policyKindSchema,
    locale: z.enum(["id", "en"]),
    contentPath: policySourcePathSchema,
    contentHash: sha256HexSchema,
});
export const historicalPolicyAnchorSchema = z.strictObject({
    id: boundedIdentifierSchema,
    kind: policyKindSchema,
    version: policyVersionSchema,
    locale: z.enum(["id", "en"]),
    contentHash: sha256HexSchema,
});
const policyReleaseSchema = z.strictObject({
    id: boundedIdentifierSchema,
    version: policyVersionSchema,
    sequence: z.number().int().min(1).max(Number.MAX_SAFE_INTEGER),
    effectiveAt: z.iso.datetime({ offset: true }),
    environment: policyEnvironmentSchema,
    status: policyReleaseStatusSchema,
    approved: z.boolean(),
    publishable: z.boolean(),
    stagingFixture: z.boolean(),
    technicalAcceptanceOnly: z.boolean(),
    requiredKinds: z.array(policyKindSchema).min(1).max(3),
    requiredLocales: z.tuple([z.literal("id"), z.literal("en")]),
    documents: z.array(policyRegistryDocumentSchema).min(2).max(6),
});
const reviewPackSchema = z.strictObject({
    id: boundedIdentifierSchema,
    status: z.literal("inactive"),
    publicationAllowed: z.literal(false),
    localeMode: z.literal("parallel_bilingual_review_only"),
    paths: z.array(policySourcePathSchema).min(1).max(20),
});
export const policyRegistrySchema = z
    .strictObject({
    schema: z.literal("tutorlite.policy-registry/v1"),
    runtimeEnvironment: policyEnvironmentSchema,
    releases: z.array(policyReleaseSchema).min(1).max(100),
    historicalAnchors: z.array(historicalPolicyAnchorSchema).max(500),
    reviewPacks: z.array(reviewPackSchema).max(20),
})
    .superRefine(validateRegistry);
const bundledDocumentSchema = policyRegistryDocumentSchema.extend({ content: policyContentSchema });
const bundledReleaseSchema = policyReleaseSchema.extend({
    documents: z.array(bundledDocumentSchema).min(2).max(6),
});
export const policyBundleSchema = z
    .strictObject({
    schema: z.literal("tutorlite.policy-bundle/v1"),
    runtimeEnvironment: policyEnvironmentSchema,
    releases: z.array(bundledReleaseSchema).min(1).max(100),
    historicalAnchors: z.array(historicalPolicyAnchorSchema).max(500),
    reviewPacks: z.array(reviewPackSchema).max(20),
})
    .superRefine((bundle, context) => {
    validateRegistry(bundle, context);
    for (const [releaseIndex, release] of bundle.releases.entries()) {
        const actionFlags = new Map();
        for (const [documentIndex, document] of release.documents.entries()) {
            const content = document.content;
            if (content.kind !== document.kind ||
                content.locale !== document.locale ||
                content.version !== release.version ||
                content.release.environment !== release.environment ||
                content.release.approved !== release.approved ||
                content.release.publishable !== release.publishable ||
                content.release.stagingFixture !== release.stagingFixture ||
                content.release.technicalAcceptanceOnly !== release.technicalAcceptanceOnly) {
                context.addIssue({
                    code: "custom",
                    path: ["releases", releaseIndex, "documents", documentIndex, "content"],
                    message: "Content identity or release flags do not match its registry release",
                });
            }
            const expectedFlags = actionFlags.get(document.kind);
            const actualFlags = {
                acceptanceAllowed: content.release.acceptanceAllowed,
                consentAllowed: content.release.consentAllowed,
                allowedEmailHashes: content.release.allowedEmailHashes,
            };
            if (expectedFlags && JSON.stringify(expectedFlags) !== JSON.stringify(actualFlags)) {
                context.addIssue({
                    code: "custom",
                    path: ["releases", releaseIndex, "documents", documentIndex, "content", "release"],
                    message: "Locale documents must use identical action flags and email hashes",
                });
            }
            else {
                actionFlags.set(document.kind, actualFlags);
            }
        }
    }
});
function isNormalizedRelativePath(value) {
    if (value.startsWith("/") ||
        value.startsWith("./") ||
        value.includes("\\") ||
        /%[0-9a-f]{2}/i.test(value))
        return false;
    const segments = value.split("/");
    return (!value.includes("//") &&
        segments.every((segment) => segment !== "" && segment !== "." && segment !== ".."));
}
function validateRegistry(registry, context) {
    const releaseIds = new Set();
    const releaseVersions = new Set();
    const anchorIds = new Set();
    const documentKeys = new Set();
    const environmentSequences = new Map();
    for (const [releaseIndex, release] of registry.releases.entries()) {
        addUniqueIssue(releaseIds, release.id, context, ["releases", releaseIndex, "id"], "Duplicate release id");
        addUniqueIssue(releaseVersions, `${release.environment}:${release.version}`, context, ["releases", releaseIndex, "version"], "Duplicate release version");
        const sequences = environmentSequences.get(release.environment) ?? new Set();
        addUniqueIssue(sequences, release.sequence, context, ["releases", releaseIndex, "sequence"], "Duplicate release sequence");
        environmentSequences.set(release.environment, sequences);
        if (new Set(release.requiredKinds).size !== release.requiredKinds.length) {
            context.addIssue({
                code: "custom",
                path: ["releases", releaseIndex, "requiredKinds"],
                message: "Duplicate required kind",
            });
        }
        if (release.publishable &&
            (!release.approved || release.stagingFixture || release.technicalAcceptanceOnly)) {
            context.addIssue({
                code: "custom",
                path: ["releases", releaseIndex],
                message: "Publishable release flags are inconsistent",
            });
        }
        if (release.technicalAcceptanceOnly &&
            (release.environment !== "staging" ||
                release.approved ||
                release.publishable ||
                !release.stagingFixture)) {
            context.addIssue({
                code: "custom",
                path: ["releases", releaseIndex],
                message: "Technical release flags are inconsistent",
            });
        }
        if (release.environment === "production" &&
            release.status === "active" &&
            (!release.approved ||
                !release.publishable ||
                release.stagingFixture ||
                release.technicalAcceptanceOnly)) {
            context.addIssue({
                code: "custom",
                path: ["releases", releaseIndex],
                message: "Active production releases must be approved and publishable",
            });
        }
        const expected = new Set(release.requiredKinds.flatMap((kind) => release.requiredLocales.map((locale) => `${kind}:${locale}`)));
        const actual = new Set();
        for (const [documentIndex, document] of release.documents.entries()) {
            addUniqueIssue(anchorIds, document.id, context, ["releases", releaseIndex, "documents", documentIndex, "id"], "Duplicate anchor id");
            const key = `${release.version}:${document.kind}:${document.locale}`;
            addUniqueIssue(documentKeys, key, context, ["releases", releaseIndex, "documents", documentIndex], "Duplicate kind/version/locale");
            actual.add(`${document.kind}:${document.locale}`);
        }
        if (actual.size !== expected.size || [...expected].some((key) => !actual.has(key))) {
            context.addIssue({
                code: "custom",
                path: ["releases", releaseIndex, "documents"],
                message: "Release documents do not satisfy required kind/locale completeness",
            });
        }
    }
    for (const [anchorIndex, anchor] of registry.historicalAnchors.entries()) {
        addUniqueIssue(anchorIds, anchor.id, context, ["historicalAnchors", anchorIndex, "id"], "Duplicate or reused historical anchor id");
        addUniqueIssue(documentKeys, `${anchor.version}:${anchor.kind}:${anchor.locale}`, context, ["historicalAnchors", anchorIndex], "Duplicate historical kind/version/locale identity");
    }
    for (const environment of ["staging", "production"]) {
        const ordered = [...registry.releases]
            .filter((release) => release.environment === environment)
            .sort((left, right) => left.sequence - right.sequence);
        for (let index = 1; index < ordered.length; index++) {
            const previous = ordered[index - 1];
            const current = ordered[index];
            if (previous &&
                current &&
                Date.parse(current.effectiveAt) <= Date.parse(previous.effectiveAt)) {
                context.addIssue({
                    code: "custom",
                    path: ["releases"],
                    message: "Release effective times must increase with sequence",
                });
            }
        }
    }
    const reviewPackIds = new Set();
    for (const [index, reviewPack] of registry.reviewPacks.entries()) {
        addUniqueIssue(reviewPackIds, reviewPack.id, context, ["reviewPacks", index, "id"], "Duplicate review pack id");
        if (new Set(reviewPack.paths).size !== reviewPack.paths.length) {
            context.addIssue({
                code: "custom",
                path: ["reviewPacks", index, "paths"],
                message: "Duplicate review pack path",
            });
        }
    }
}
function addUniqueIssue(values, value, context, path, message) {
    if (values.has(value))
        context.addIssue({ code: "custom", path, message });
    values.add(value);
}
//# sourceMappingURL=policy.js.map